import { GoogleAuthFlowError } from './googleAuthFlow';
import { GOOGLE_SIGN_IN_CLIENTS } from './googleCredentialClients';

type GoogleIdCredentials = { credential?: string };

type GoogleNotification = {
  isNotDisplayed?: () => boolean;
  isSkippedMoment?: () => boolean;
};

type GoogleIdentityApi = {
  initialize: (options: {
    client_id: string;
    callback: (response: GoogleIdCredentials) => void;
    nonce: string;
    auto_select: boolean;
    ux_mode: 'popup' | 'redirect';
  }) => void;
  prompt: (callback: (notification: GoogleNotification) => void) => void;
  cancel: () => void;
};

type GoogleAccountsObject = {
  id: GoogleIdentityApi;
};

type GoogleWindow = {
  google?: {
    accounts?: GoogleAccountsObject;
  };
};

type ScriptLike = {
  id: string;
  src: string;
  async: boolean;
  onload: (() => void) | null;
  onerror: (() => void) | null;
  remove: () => void;
};

type DocumentLike = {
  head: { appendChild: (node: ScriptLike) => void };
  getElementById: (id: string) => ScriptLike | null;
  createElement: (tag: 'script') => ScriptLike;
};

const GIS_SCRIPT_ID = 'google-sign-in-gsi-script';
const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GIS_TIMEOUT_MS = 15_000;
const POLL_MS = 80;
let loadState: Promise<void> | null = null;

function getGoogleIdentity(): GoogleIdentityApi | null {
  const accounts = (globalThis as GoogleWindow).google?.accounts;
  if (!accounts?.id) return null;
  if (!accounts.id.initialize || !accounts.id.prompt || !accounts.id.cancel) return null;
  return accounts.id;
}

function waitForReady(resolve: () => void, reject: (error: GoogleAuthFlowError) => void) {
  let poll: ReturnType<typeof setTimeout> | undefined;
  const timer = setTimeout(() => {
    clearTimeout(poll);
    reject(new GoogleAuthFlowError('unavailable'));
  }, GIS_TIMEOUT_MS);
  const check = () => {
    if (getGoogleIdentity()) {
      clearTimeout(timer);
      resolve();
      return;
    }
    poll = setTimeout(check, POLL_MS);
  };
  check();
}

function loadGisScript(): Promise<void> {
  if (getGoogleIdentity()) return Promise.resolve();
  if (loadState) return loadState;

  const pending = new Promise<void>((resolve, reject) => {
    const document = (globalThis as typeof globalThis & { document?: DocumentLike }).document;
    if (!document) {
      reject(new GoogleAuthFlowError('unavailable'));
      return;
    }

    const existing = document.getElementById(GIS_SCRIPT_ID);
    if (existing) {
      waitForReady(resolve, reject);
      return;
    }

    const script = document.createElement('script');
    script.id = GIS_SCRIPT_ID;
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      script.onload = null;
      script.onerror = null;
      if (!ready) {
        script.remove();
        reject(new GoogleAuthFlowError('unavailable'));
        return;
      }
      resolve();
    };
    const timeout = setTimeout(() => finish(false), GIS_TIMEOUT_MS);
    script.onload = () => finish(!!getGoogleIdentity());
    script.onerror = () => finish(false);
    try {
      document.head.appendChild(script);
    } catch {
      finish(false);
    }
  });

  // Reset after settlement, including synchronous DOM failures in the executor.
  // A late callback from an older script cannot clear a newer loading attempt.
  const shared = pending.then(
    () => { if (loadState === shared) loadState = null; },
    () => {
      if (loadState === shared) loadState = null;
      throw new GoogleAuthFlowError('unavailable');
    },
  );
  loadState = shared;
  return shared;
}

export async function requestGoogleCredential(nonce: string): Promise<string | null> {
  await loadGisScript();
  const identity = getGoogleIdentity();
  if (!identity) throw new GoogleAuthFlowError('unavailable');

  return new Promise((resolve, reject) => {
    let handled = false;
    const timeout = setTimeout(() => fail(), GIS_TIMEOUT_MS);

    const finalize = (value: string | null) => {
      if (handled) return;
      handled = true;
      clearTimeout(timeout);
      resolve(value);
    };

    const fail = () => {
      if (handled) return;
      handled = true;
      clearTimeout(timeout);
      // Provider cleanup must not strand the request or expose a raw SDK error.
      // Mark it handled first: cancel may synchronously invoke old callbacks.
      try {
        identity.cancel();
      } catch {
        // The local failure remains authoritative if provider cleanup fails.
      }
      reject(new GoogleAuthFlowError('unavailable'));
    };

    try {
      identity.initialize({
        client_id: GOOGLE_SIGN_IN_CLIENTS.web,
        callback: (response) => {
          finalize(typeof response?.credential === 'string' ? response.credential : null);
        },
        nonce,
        auto_select: false,
        ux_mode: 'popup',
      });
      identity.prompt((notification) => {
        const notDisplayed = notification?.isNotDisplayed?.();
        const skipped = notification?.isSkippedMoment?.();
        if (notDisplayed || skipped) {
          finalize(null);
        }
      });
    } catch {
      fail();
    }
  });
}
